import getAblyClient from './ably/AblyClient';
import { mapRawAblyMessage } from './messageTypes';
import { userInfoSync } from 'hub-http/userInfo';

/**
 * Takes the form `crm-record:{portalId}:{objectTypeId}:{objectId}
 */

const makeChannelId = (objectTypeId, objectId) => {
  const portalId = userInfoSync().portal.portal_id;
  return `crm-record:${portalId}:${objectTypeId}:${objectId}`;
};
const makeChannelMessageTypeKey = (channelId, messageType) => {
  return `${channelId}::${messageType}`;
};
const makeClientId = (portalId, userId) => {
  return `${portalId}:${userId}`;
};

/**
 * Manages Ably channel subscriptions for CRM records.
 *
 * - Deduplicates subscriptions per (channelId, messageType) by maintaining a single underlying
 *   Ably subscription and fanning out messages to all registered handlers.
 * - Stores the exact dispatcher in `channelMessageTypeToOnMessage` so we can later
 *   call `channel.unsubscribe(messageType, dispatcher)` with the same reference when
 *   the last handler is removed.
 * - Ensures correct cleanup, prevents duplicate subscriptions, and avoids memory leaks.
 */
class CrmRecordChannelManager {
  /**
   * Keeps a single dispatcher function per `${channelId}::${messageType}` that was passed
   * to `channel.subscribe(...)`. This enables correct `channel.unsubscribe(messageType, dispatcher)`
   * when no handlers remain for that message type on the channel.
   */

  constructor() {
    this.channelIdToEntry = new Map();
    this.channelMessageTypeToOnMessage = new Map();
    const userInfo = userInfoSync();
    this.portalId = userInfo.portal.portal_id;
    this.userId = userInfo.user.user_id;
  }
  ensureEntry(channelId) {
    const existing = this.channelIdToEntry.get(channelId);
    if (existing) return existing;
    const entry = {
      clientId: makeClientId(this.portalId, this.userId),
      messageTypeToHandlers: new Map(),
      isPresenceEntered: false
    };
    this.channelIdToEntry.set(channelId, entry);
    return entry;
  }
  async subscribe(objectTypeId, objectId, messageTypes, handler) {
    const client = await getAblyClient();
    const channelId = makeChannelId(objectTypeId, objectId);
    const entry = this.ensureEntry(channelId);
    const channel = client.realtime.channels.get(channelId);
    if (!entry.isPresenceEntered) {
      await new Promise((resolve, reject) => {
        channel.presence.enter(entry.clientId, err => {
          if (err) {
            console.error(`Error entering presence for channel ${channelId}`, err);
            reject(err);
          }
          resolve();
        });
      });
      entry.isPresenceEntered = true;
    }
    messageTypes.forEach(messageType => {
      let handlers = entry.messageTypeToHandlers.get(messageType);
      if (!handlers) {
        handlers = new Set();
        entry.messageTypeToHandlers.set(messageType, handlers);
      }
      handlers.add(handler);
      const key = makeChannelMessageTypeKey(channelId, messageType);
      if (!this.channelMessageTypeToOnMessage.has(key)) {
        const onMessage = message => {
          const set = entry.messageTypeToHandlers.get(messageType);
          if (!set || set.size === 0) return;
          const mapped = mapRawAblyMessage(message);
          set.forEach(h => h(mapped));
        };
        this.channelMessageTypeToOnMessage.set(key, onMessage);
        channel.subscribe(messageType, onMessage);
      }
    });
    return () => {
      messageTypes.forEach(messageType => {
        const handlers = entry.messageTypeToHandlers.get(messageType);
        if (handlers) {
          handlers.delete(handler);
          if (handlers.size === 0) {
            entry.messageTypeToHandlers.delete(messageType);
            const key = makeChannelMessageTypeKey(channelId, messageType);
            const onMessage = this.channelMessageTypeToOnMessage.get(key);
            if (onMessage) {
              channel.unsubscribe(messageType, onMessage);
              this.channelMessageTypeToOnMessage.delete(key);
            }
          }
        }
      });
      const hasAnyHandlers = Array.from(entry.messageTypeToHandlers.values()).some(s => s.size > 0);
      if (!hasAnyHandlers) {
        channel.presence.leave(entry.clientId, () => {});
        entry.isPresenceEntered = false;
        this.channelIdToEntry.delete(channelId);
      }
    };
  }
}
export const getCrmRecordChannelManager = (() => {
  let crmRecordChannelManager = null;
  return () => {
    if (!crmRecordChannelManager) {
      crmRecordChannelManager = new CrmRecordChannelManager();
    }
    return crmRecordChannelManager;
  };
})();